import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import documentService from '../../services/documentService';
import questionService, { Question } from '../../services/questionService';
import QuestionEditForm from '../../components/questions/QuestionEditForm';
import ContentRenderer from '../../components/common/ContentRenderer';
import { getFileTypeName, renderFileTypeIcon, formatDate } from '../../config/fileUtils';

const DocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [processingDialog, setProcessingDialog] = useState<boolean>(false);
  const [processingLoading, setProcessingLoading] = useState<boolean>(false);
  const [numQuestions] = useState<number>(10);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Edit question states
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editFormOpen, setEditFormOpen] = useState<boolean>(false);

  // Delete question states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);

  // Fetch document on component mount
  useEffect(() => {
    if (id) {
      fetchDocument(parseInt(id));
    }
    
    // Clean up interval on unmount
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [id]);

  // Fetch document details
  const fetchDocument = async (documentId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.getDocumentById(documentId);
      setDocument(response.document);
      
      // If document is processed, fetch questions
      if (response.document.processed) {
        fetchQuestions(documentId);
      } else if (response.document.processing_status === 'processing' || response.document.processing_status === 'pending') {
        // Start polling for status updates
        startStatusCheck(documentId);
      } else {
        // Document is not processed and not processing, stop loading
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch document');
      setLoading(false);
    }
  };

  // Fetch questions for document
  const fetchQuestions = async (documentId: number) => {
    try {
      const questions = await questionService.getQuestionsByDocument(documentId);
      setQuestions(questions);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch questions');
      setLoading(false);
    }
  };

  // Start processing document
  const startProcessing = async () => {
    if (!document || !id) return;
    
    setProcessingLoading(true);
    setError(null);
    setProcessingStatus(null); // Reset processing status when starting new processing
    
    try {
      const response = await documentService.processDocument(parseInt(id), numQuestions);
      
      if (response.success) {
        setProcessingDialog(false);
        setProcessingStatus('processing');
        startStatusCheck(parseInt(id));
      } else {
        setError('Failed to start processing');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start processing');
    } finally {
      setProcessingLoading(false);
    }
  };

  // Start status check interval
  const startStatusCheck = (documentId: number) => {
    // Clear any existing interval
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }
    
    // Set processing status
    setProcessingStatus('processing');
    
    // Start new interval
    const interval = setInterval(async () => {
      try {
        const response = await documentService.checkProcessingStatus(documentId);
        
        if (response.processing_status === 'completed') {
          // Document processing completed
          clearInterval(interval);
          setStatusCheckInterval(null);
          setProcessingStatus('completed');
          
          // Update document and fetch questions
          fetchDocument(documentId);
        } else if (response.processing_status === 'failed') {
          // Document processing failed
          clearInterval(interval);
          setStatusCheckInterval(null);
          setProcessingStatus('failed');
          setLoading(false); // Set loading to false when processing fails
          setError('Document processing failed');
        }
      } catch (err) {
        console.error('Error checking status:', err);
        // If there's an error checking status, stop the interval and set error state
        clearInterval(interval);
        setStatusCheckInterval(null);
        setProcessingStatus('failed');
        setLoading(false);
        setError('Error checking processing status. Please refresh the page or try again.');
      }
    }, 3000); // Check every 3 seconds
    
    setStatusCheckInterval(interval);
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/documents');
  };

  // Handle edit question
  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setEditFormOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    // Refresh questions after successful edit
    if (id) {
      fetchQuestions(parseInt(id));
    }
  };

  // Handle close edit form
  const handleCloseEditForm = () => {
    setEditFormOpen(false);
    setEditingQuestion(null);
  };

  // Handle delete question
  const handleDeleteQuestion = (question: Question) => {
    setQuestionToDelete(question);
    setDeleteConfirmOpen(true);
  };

  // Handle close delete dialog
  const handleCloseDeleteDialog = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setQuestionToDelete(null);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;
    
    setDeleting(true);
    setError(null);
    try {
      await questionService.deleteQuestion(questionToDelete.id);
      
      // Successfully deleted
      setDeleteConfirmOpen(false);
      setQuestionToDelete(null);
      setDeleting(false);
      setDeleteSuccess(true);
      
      // Refresh questions list
      fetchQuestions(parseInt(id!));
      
      // Hide success message after 3 seconds
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete question');
      setDeleting(false);
    }
  };

  // Get question difficulty label and color
  const getDifficultyInfo = (difficulty: number | null) => {
    if (difficulty === null) return { label: 'Không xác định', color: 'default' };
    
    if (difficulty < 0.3) return { label: 'Dễ', color: 'success' };
    if (difficulty < 0.7) return { label: 'Trung bình', color: 'warning' };
    return { label: 'Khó', color: 'error' };
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Quay lại Danh sách Tài liệu
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {deleteSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Xóa câu hỏi thành công!
          </Alert>
        )}

        {loading && !document ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : document ? (
          <>
            {/* Document Details */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2 }}>
                  {renderFileTypeIcon(document.file_type)}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h1">
                    {document.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Tải lên vào {formatDate(document.created_at)}
                  </Typography>
                </Box>
                <Box>
                  {!document.processed && !processingStatus && (
                    <Tooltip title="Tạo Câu hỏi">
                      <IconButton
                        color="primary"
                        onClick={() => setProcessingDialog(true)}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}>
                  <Typography variant="body2">
                    <strong>Loại tệp:</strong> {getFileTypeName(document.file_type)}
                  </Typography>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}>
                  <Typography variant="body2" sx={{ pointerEvents: 'none' }}>
                    <strong>Trạng thái:</strong>{' '}
                    <Chip
                      label={
                        processingStatus === 'completed' || document.processed
                          ? 'Hoàn thành'
                          : processingStatus === 'processing' || document.processing_status === 'processing'
                          ? 'Đang xử lý'
                          : processingStatus === 'failed'
                          ? 'Thất bại'
                          : 'Chờ xử lý'
                      }
                      color={
                        (processingStatus === 'completed' || document.processed)
                          ? 'success'
                          : processingStatus === 'failed'
                          ? 'error'
                          : 'warning'
                      }
                      size="small"
                      clickable={false}
                    />
                  </Typography>
                </Box>
              </Box>

              {(processingStatus === 'processing' || document.processing_status === 'processing') && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Đang xử lý tài liệu...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}
            </Paper>

            {/* Questions */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                  Câu hỏi đã tạo
                </Typography>
                {document.processed && (
                  <Tooltip title="Làm mới danh sách câu hỏi">
                    <IconButton
                      color="primary"
                      onClick={() => fetchQuestions(parseInt(id!))}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {!document.processed && !processingStatus ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="textSecondary">
                    Tài liệu này chưa được xử lý. Hãy tạo câu hỏi để bắt đầu.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => setProcessingDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Tạo Câu hỏi
                  </Button>
                </Box>
              ) : processingStatus === 'processing' || document.processing_status === 'processing' ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="textSecondary">
                    Đang xử lý tài liệu và tạo câu hỏi...
                  </Typography>
                </Box>
              ) : processingStatus === 'failed' ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="error" gutterBottom>
                    Xử lý tài liệu thất bại. Vui lòng thử lại.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => setProcessingDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Thử lại
                  </Button>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : questions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="textSecondary">
                    Không tìm thấy câu hỏi nào cho tài liệu này.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {questions.map((question, index) => {
                    const difficultyInfo = getDifficultyInfo(question.difficulty);
                    
                    return (
                      <Box key={question.id}>
                        <Card variant="outlined">
                          <CardHeader
                            title={`Câu hỏi ${index + 1}`}
                            subheader={
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, pointerEvents: 'none' }}>
                                <Chip
                                  label={
                                    question.type === 'multiple_choice' ? 'Trắc nghiệm' :
                                    question.type === 'true_false' ? 'Đúng/Sai' :
                                    question.type === 'short_answer' ? 'Trả lời ngắn' :
                                    question.type === 'fill_in_blank' ? 'Điền từ' :
                                    'Không xác định'
                                  }
                                  size="small"
                                  color="primary"
                                  sx={{ mr: 1 }}
                                />
                                <Chip
                                  label={difficultyInfo.label}
                                  size="small"
                                  color={difficultyInfo.color as any}
                                />
                                {question.tags && Array.isArray(question.tags) && question.tags.length > 0 && (
                                  <Box sx={{ ml: 1 }}>
                                    {question.tags.map((tag, i) => (
                                      <Chip
                                        key={i}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        sx={{ ml: 0.5 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            }
                                                          action={
                                <>
                                  <Tooltip title="Chỉnh sửa câu hỏi">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditQuestion(question)}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Xóa câu hỏi">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteQuestion(question)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              }
                          />
                          <CardContent>
                            <Typography variant="body1" gutterBottom>
                              <ContentRenderer text={question.text || 'Không có nội dung câu hỏi'} />
                            </Typography>
                            
                            <List dense>
                              {question.options && Array.isArray(question.options) && question.options.map((option, optionIndex) => {
                                const isCorrect = question.correct_answer === (optionIndex + 1);
                                return (
                                  <ListItem key={optionIndex}>
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          color={
                                            isCorrect
                                              ? 'success.main'
                                              : 'textPrimary'
                                          }
                                          sx={{
                                            fontWeight:
                                              isCorrect
                                                ? 'bold'
                                                : 'normal',
                                          }}
                                        >
                                          {String.fromCharCode(65 + optionIndex)}. <ContentRenderer text={option || 'Không có nội dung lựa chọn'} />
                                          {isCorrect && ' ✓'}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                            
                            {question.explanation && (
                              <Box sx={{ mt: 2, backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                  Giải thích:
                                </Typography>
                                <Typography variant="body2">
                                  <ContentRenderer text={question.explanation} />
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </>
        ) : (
          <Alert severity="error">Không tìm thấy tài liệu</Alert>
        )}
      </Box>

      {/* Processing Dialog */}
      <Dialog open={processingDialog} onClose={() => !processingLoading && setProcessingDialog(false)}>
        <DialogTitle>Tạo Câu hỏi</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            AI sẽ phân tích tài liệu này và tạo ra các câu hỏi trắc nghiệm dựa trên nội dung của nó.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Quá trình này có thể mất vài phút tùy thuộc vào kích thước và độ phức tạp của tài liệu.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => !processingLoading && setProcessingDialog(false)}
            disabled={processingLoading}
          >
            Hủy
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={startProcessing}
            disabled={processingLoading}
            startIcon={processingLoading ? <CircularProgress size={20} /> : null}
          >
            {processingLoading ? 'Đang xử lý...' : 'Tạo Câu hỏi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Edit Form */}
      <QuestionEditForm
        open={editFormOpen}
        question={editingQuestion}
        onClose={handleCloseEditForm}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Xóa Câu hỏi"}</DialogTitle>
        <DialogContent>
          <Typography>Bạn có chắc chắn muốn xóa câu hỏi này? Hành động này không thể hoàn tác.</Typography>
          {questionToDelete && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Câu hỏi: {questionToDelete.text?.substring(0, 100)}...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DocumentDetail; 